import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { auth, conversations, clients } from '../api/client'

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function parseDate(dateString) {
  if (!dateString) return null
  let date = new Date(dateString)
  if (isNaN(date.getTime())) {
    const match = dateString.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})/)
    if (match) {
      date = new Date(match[1] + 'T' + match[2] + 'Z')
    }
  }
  return isNaN(date.getTime()) ? null : date
}

function formatTime(dateString) {
  const date = parseDate(dateString)
  if (!date) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateString) {
  const date = parseDate(dateString)
  if (!date) return ''
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

// ============================================================================
// MARKDOWN COMPONENTS
// ============================================================================

const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-lume-teal">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="ml-2">{children}</li>,
  h1: ({ children }) => <h1 className="text-xl font-bold mb-2 text-gray-900 dark:text-lume-teal">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold mb-2 text-gray-900 dark:text-lume-teal">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mb-1 text-gray-900 dark:text-lume-teal">{children}</h3>,
  code: ({ inline, children }) =>
    inline
      ? <code className="bg-gray-200 dark:bg-dial-black text-gray-800 dark:text-lume-teal px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      : <code className="block bg-gray-200 dark:bg-dial-black text-gray-800 dark:text-lume-teal p-3 rounded text-sm font-mono overflow-x-auto mb-2">{children}</code>,
  pre: ({ children }) => <pre className="bg-gray-200 dark:bg-dial-black text-gray-800 dark:text-lume-teal p-3 rounded overflow-x-auto mb-2">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-400 dark:border-lume-teal pl-3 italic mb-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} className="text-primary-600 dark:text-lume-teal underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  hr: () => <hr className="my-3 border-0 h-px" style={{ backgroundColor: 'rgba(0, 217, 160, 0.4)' }} />,
}

const userMarkdownComponents = {
  ...markdownComponents,
  code: ({ inline, children }) =>
    inline
      ? <code className="bg-primary-500 dark:bg-dial-surface dark:text-lume-green px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      : <code className="block bg-primary-500 dark:bg-dial-surface dark:text-lume-green p-3 rounded text-sm font-mono overflow-x-auto mb-2">{children}</code>,
  pre: ({ children }) => <pre className="bg-primary-500 dark:bg-dial-surface dark:text-lume-green p-3 rounded overflow-x-auto mb-2">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-primary-300 dark:border-lume-teal pl-3 italic mb-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} className="text-primary-200 dark:text-lume-green underline" target="_blank" rel="noopener noreferrer">{children}</a>,
}

// ============================================================================
// TOOL INDICATOR COMPONENT
// ============================================================================

function ToolIndicator({ tools }) {
  if (!tools || tools.length === 0) return null

  return (
    <div className="flex justify-start mb-3 animate-fade-in">
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 rounded-xl px-4 py-3 max-w-[85%]">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Working</span>
        </div>
        <div className="space-y-1.5">
          {tools.map((tool, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
              {tool.status === 'running' ? (
                <svg className="w-4 h-4 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : tool.status === 'done' ? (
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <div>
                <span>{tool.description}</span>
                {tool.status === 'error' && tool.error && (
                  <div className="text-xs text-red-400 dark:text-red-400 mt-0.5 font-mono">{tool.error}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const TOOL_LABELS = {
  create_knowledge: 'KB Updated',
  save_memory: 'Memory Saved',
  create_workout: 'Workout Created',
  create_workout_program: 'Program Created',
  add_program_phase: 'Phase Added',
  assign_phase_workout: 'Workout Assigned',
  activate_program: 'Program Activated',
  generate_program_pdf: 'PDF Generated',
  log_weight: 'Weight Logged',
  log_meal: 'Meal Logged',
  log_recovery: 'Recovery Logged',
  log_body_measurements: 'Measurements Logged',
  escalate_to_trainer: 'Escalated to Trainer',
  emergency_alert: 'Emergency Alert Sent',
  mark_workout_complete: 'Workout Completed',
  get_client_stats: 'Checked Stats',
  get_workout_history: 'Checked Workouts',
  search_knowledge: 'Searched KB',
  recall_memories: 'Recalled Memories',
  save_progress_photo: 'Photo Saved',
  analyze_food_photo: 'Photo Analyzed',
  present_ped_waiver: 'PED Waiver Presented',
  update_client_goals: 'Goals Updated',
  mark_onboarding_complete: 'Onboarding Complete',
}

function ToolPills({ tools }) {
  if (!tools || tools.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {tools.map((tool, i) => {
        const isError = tool.status === 'error'
        return (
          <span
            key={i}
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-mono ${
              isError
                ? 'bg-red-500/15 text-red-400'
                : 'bg-gray-100 text-gray-500 dark:bg-dial-elevated dark:text-indices-muted'
            }`}
            title={isError && tool.error ? tool.error : undefined}
          >
            {isError ? (
              <svg className="w-2.5 h-2.5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-2.5 h-2.5 text-green-500 dark:text-lume-teal flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {isError
              ? `${TOOL_LABELS[tool.name] || tool.name}: ${tool.error || 'Failed'}`
              : (TOOL_LABELS[tool.name] || tool.description || tool.name)
            }
          </span>
        )
      })}
    </div>
  )
}

// ============================================================================
// SUGGESTED ACTIONS COMPONENT
// ============================================================================

function SuggestedActions({ suggestions, onSelect }) {
  if (!suggestions || suggestions.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 mb-3 animate-fade-in">
      {suggestions.map((suggestion, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(suggestion.prompt)}
          className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-primary-300 text-gray-700 transition-colors flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          {suggestion.label}
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// MESSAGE COMPONENT WITH ACTIONS
// ============================================================================

function Message({ message, isUser, isNew = false, isStreaming = false, onCopy, onRegenerate }) {
  const [showActions, setShowActions] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    onCopy?.()
  }

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 ${!isUser && isNew ? 'animate-fade-in' : ''} group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onFocus={() => setShowActions(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowActions(false) }}
    >
      <div className="relative max-w-[92%]">
        <div
          className={`px-4 py-2 rounded-2xl ${
            isUser
              ? 'text-white rounded-br-md'
              : 'bg-gray-200 dark:bg-dial-black text-gray-900 dark:text-lume-teal rounded-bl-md'
          }`}
        >
          {/* Multi-file attachment preview */}
          {message.files && message.files.length > 0 && (
            <div className={`mb-2 p-2 rounded-lg ${isUser ? 'bg-primary-500 dark:bg-dial-black' : 'bg-gray-300 dark:bg-dial-surface'}`}>
              <div className="flex flex-wrap gap-2">
                {message.files.map((file, index) => (
                  <div key={index}>
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="max-w-[120px] max-h-24 rounded object-contain"
                      />
                    ) : (
                      <div className="flex items-center gap-2 px-2 py-1 bg-black/10 dark:bg-white/10 rounded">
                        <svg className={`w-4 h-4 ${isUser ? 'text-primary-200 dark:text-white/60' : 'text-gray-500 dark:text-indices-muted'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className={`text-xs ${isUser ? 'text-primary-100 dark:text-white/80' : 'text-gray-700 dark:text-indices-medium'}`}>
                          {file.name}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Legacy single file support */}
          {message.file && !message.files && (
            <div className={`mb-2 p-2 rounded-lg ${isUser ? 'bg-primary-500 dark:bg-dial-black' : 'bg-gray-300 dark:bg-dial-surface'}`}>
              {message.file.preview ? (
                <img
                  src={message.file.preview}
                  alt={message.file.name}
                  className="max-w-full max-h-48 rounded object-contain"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <svg className={`w-5 h-5 ${isUser ? 'text-primary-200 dark:text-white/60' : 'text-gray-500 dark:text-indices-muted'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className={`text-sm ${isUser ? 'text-primary-100 dark:text-white/80' : 'text-gray-700 dark:text-indices-medium'}`}>
                    {message.file.name}
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Persisted attachment from history */}
          {message.attachmentUrl && !message.file && !message.files && message.attachmentMimeType?.startsWith('image/') && (
            <div className="mb-2">
              <img
                src={`${window.location.pathname.match(/^(.*\/WebObjects\/forge\.woa)/)?.[1] ?? ''}/wa/chatAttachment?path=${encodeURIComponent(message.attachmentUrl)}`}
                alt="Attached image"
                className="max-w-full max-h-64 rounded-lg object-contain cursor-pointer"
                onClick={(e) => window.open(e.target.src, '_blank')}
                loading="lazy"
              />
            </div>
          )}
          <div className="prose dark:prose-invert max-w-none">
            {isStreaming ? (
              <p className="whitespace-pre-wrap">{message.content}</p>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={isUser ? userMarkdownComponents : markdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
          <p className={`text-xs mt-1 ${isUser ? 'text-primary-200 dark:text-lume-teal/50' : 'text-gray-500 dark:text-lume-teal/40'}`}>
            {formatTime(message.timestamp)}
          </p>
          {!isUser && message.tools && <ToolPills tools={message.tools} />}
        </div>

        {/* Message Actions */}
        {showActions && (
          <div className={`absolute top-0 ${isUser ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} flex items-center gap-1`}>
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg bg-white dark:bg-dial-elevated shadow-sm border border-gray-200 dark:border-dial-border text-gray-500 dark:text-indices-muted hover:text-gray-700 dark:hover:text-indices-bright hover:bg-gray-50 dark:hover:bg-dial-surface transition-colors"
              title="Copy"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            {!isUser && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="p-1.5 rounded-lg bg-white shadow-sm border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                title="Regenerate"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// TYPING INDICATOR
// ============================================================================

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3 animate-fade-in">
      <div className="bg-gray-200 dark:bg-dial-elevated text-gray-900 dark:text-indices-bright rounded-2xl rounded-bl-md px-4 py-3">
        <div className="flex items-center space-x-1.5">
          <div className="w-2.5 h-2.5 bg-gray-400 dark:bg-indices-muted rounded-full animate-typing-dot" style={{ animationDelay: '0ms' }} />
          <div className="w-2.5 h-2.5 bg-gray-400 dark:bg-indices-muted rounded-full animate-typing-dot" style={{ animationDelay: '200ms' }} />
          <div className="w-2.5 h-2.5 bg-gray-400 dark:bg-indices-muted rounded-full animate-typing-dot" style={{ animationDelay: '400ms' }} />
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// CONVERSATION SIDEBAR
// ============================================================================

function ConversationSidebar({ conversations: convos, currentId, onSelect, onNew, isOpen, onClose }) {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-dial-surface shadow-xl z-50 flex flex-col lg:relative lg:shadow-none lg:border-r lg:border-gray-200 dark:border-dial-border">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-dial-border flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-indices-bright">Conversations</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 dark:text-indices-muted hover:text-gray-700 dark:hover:text-indices-bright lg:hidden"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* New Conversation Button */}
        <div className="p-3">
          <button
            onClick={onNew}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black rounded-lg hover:bg-primary-700 dark:hover:bg-lume-green transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Conversation
          </button>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {(!convos || convos.length === 0) ? (
            <div className="p-4 text-center text-gray-500 dark:text-indices-muted text-sm">
              No conversations yet
            </div>
          ) : (
            <div className="space-y-1 p-2">
              {convos.map((convo) => (
                <button
                  key={convo.id}
                  onClick={() => onSelect(convo)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    currentId === convo.id
                      ? 'bg-primary-50 dark:bg-lume-teal/10 border border-primary-200 dark:border-lume-teal/30'
                      : 'hover:bg-gray-100 dark:hover:bg-dial-elevated'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-indices-bright truncate text-sm">
                        {convo.title || convo.conversationType || 'Conversation'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-indices-muted mt-0.5">
                        {formatDate(convo.updatedAt || convo.createdAt)}
                      </p>
                    </div>
                    {currentId === convo.id && (
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-1.5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ============================================================================
// MENTION AUTOCOMPLETE
// ============================================================================

function MentionAutocomplete({ query, clients: clientList, onSelect, position }) {
  const filtered = useMemo(() => {
    if (!query || !clientList) return []
    const q = query.toLowerCase()
    return clientList.filter(c =>
      c.firstName?.toLowerCase().includes(q) ||
      c.lastName?.toLowerCase().includes(q) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
    ).slice(0, 5)
  }, [query, clientList])

  if (filtered.length === 0) return null

  return (
    <div
      className="absolute bottom-full mb-2 left-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
      style={position}
    >
      <div className="p-2 text-xs text-gray-500 border-b border-gray-100">
        Clients
      </div>
      {filtered.map((client) => (
        <button
          key={client.id}
          onClick={() => onSelect(client)}
          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm">
            {client.firstName?.[0]}{client.lastName?.[0]}
          </div>
          <div>
            <p className="font-medium text-gray-900 text-sm">{client.firstName} {client.lastName}</p>
            <p className="text-xs text-gray-500">{client.email}</p>
          </div>
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// SMART INPUT COMPONENT
// ============================================================================

function SmartInput({
  value,
  onChange,
  onSubmit,
  onFileSelect,
  selectedFiles,
  onClearFile,
  onClearAllFiles,
  filePreviews,
  disabled,
  isRecording,
  onVoiceToggle,
  clients: clientList,
  placeholder = "Type a message...",
}) {
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  const handleChange = (e) => {
    const newValue = e.target.value
    onChange(newValue)

    // Check for @ mention
    const lastAtIndex = newValue.lastIndexOf('@')
    if (lastAtIndex !== -1) {
      const afterAt = newValue.slice(lastAtIndex + 1)
      if (!afterAt.includes(' ')) {
        setMentionQuery(afterAt)
        setShowMentions(true)
        return
      }
    }
    setShowMentions(false)
  }

  const handleMentionSelect = (client) => {
    const lastAtIndex = value.lastIndexOf('@')
    const newValue = value.slice(0, lastAtIndex) + `@${client.firstName} ${client.lastName} `
    onChange(newValue)
    setShowMentions(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit(e)
    }
  }

  // Reset textarea height when value is cleared (after sending)
  useEffect(() => {
    if (!value && inputRef.current) {
      inputRef.current.style.height = 'auto'
    }
  }, [value])

  return (
    <div className="chat-input bg-white dark:bg-dial-surface border-t border-gray-200 dark:border-dial-border px-2 sm:px-4 py-2 sm:py-3 safe-area-inset-bottom">
      {/* Multi-file preview */}
      {selectedFiles && selectedFiles.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 dark:text-indices-muted">
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} attached
            </span>
            {selectedFiles.length > 1 && (
              <button
                type="button"
                onClick={onClearAllFiles}
                className="text-xs text-red-500 hover:text-red-600 dark:text-red-400"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative group">
                {filePreviews[index] ? (
                  <img
                    src={filePreviews[index]}
                    alt={file.name}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-dial-border"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 dark:bg-dial-elevated rounded-lg border border-gray-200 dark:border-dial-border flex flex-col items-center justify-center p-1">
                    <svg className="w-5 h-5 text-gray-400 dark:text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-[10px] text-gray-500 dark:text-indices-muted truncate w-full text-center mt-0.5">
                      {file.name.split('.').pop()?.toUpperCase()}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onClearFile(index)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="relative flex items-end gap-1 sm:gap-2">
        {/* Mention Autocomplete */}
        {showMentions && (
          <MentionAutocomplete
            query={mentionQuery}
            clients={clientList}
            onSelect={handleMentionSelect}
          />
        )}

        {/* Attach button — transparent file input overlaid on icon only */}
        <button
          type="button"
          className={`relative p-3 flex-shrink-0 ${disabled ? 'opacity-50 pointer-events-none' : 'text-gray-500 dark:text-indices-muted hover:text-gray-700 dark:hover:text-indices-bright'}`}
          title="Attach file"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileSelect}
            accept="image/*,.pdf,.csv,.txt"
            multiple
            className="hidden"
          />
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>

        {/* Text input */}
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => {
            handleChange(e)
            e.target.style.height = 'auto'
            e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 min-w-0 px-4 py-3 text-base bg-gray-100 dark:bg-dial-elevated text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted rounded-2xl border-0 focus:ring-2 focus:ring-primary-500 dark:focus:ring-lume-teal outline-none disabled:opacity-50 resize-none max-h-40 overflow-y-auto"
        />

        {/* Voice button */}
        <button
          type="button"
          onClick={onVoiceToggle}
          disabled={disabled}
          className={`p-3 rounded-full transition-colors flex-shrink-0 ${
            isRecording
              ? 'bg-red-500 text-white animate-pulse'
              : 'text-gray-500 dark:text-indices-muted hover:text-gray-700 dark:hover:text-indices-bright disabled:opacity-50'
          }`}
          title={isRecording ? "Stop recording" : "Voice input"}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        </button>

        {/* Send button */}
        <button
          type="submit"
          onMouseDown={(e) => e.preventDefault()}
          disabled={(!value.trim() && (!selectedFiles || selectedFiles.length === 0)) || disabled}
          className="p-3 bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 dark:hover:bg-lume-green transition-colors flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </form>

      {/* Hint - hidden on very small screens */}
      <p className="hidden xs:block text-xs text-gray-400 dark:text-indices-muted mt-1.5 sm:mt-2 text-center">
        Type <span className="font-mono bg-gray-100 dark:bg-dial-elevated px-1 rounded">@</span> to mention a client
      </p>
    </div>
  )
}

// ============================================================================
// FILE HANDLING
// ============================================================================

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/csv',
  'text/plain',
]

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const MAX_FILES = 10 // Maximum files per message

// ============================================================================
// MAIN CHAT PAGE COMPONENT
// ============================================================================

export default function ChatPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // State
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [currentConversationId, setCurrentConversationId] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [filePreviews, setFilePreviews] = useState([])
  const [activeTools, setActiveTools] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isRecording, setIsRecording] = useState(false)

  // Refs
  const wsRef = useRef(null)
  const pingIntervalRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const messagesEndRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const streamingContentRef = useRef('')
  const streamRafRef = useRef(null)
  const streamingToolsRef = useRef([])
  const isInitialLoad = useRef(true)
  const mediaRecorderRef = useRef(null)

  // Batched streaming update — coalesces rapid token events into one render per frame
  const flushStreamingContent = useCallback(() => {
    streamRafRef.current = null
    setStreamingContent(streamingContentRef.current)
  }, [])

  const scheduleStreamingUpdate = useCallback(() => {
    if (!streamRafRef.current) {
      streamRafRef.current = requestAnimationFrame(flushStreamingContent)
    }
  }, [flushStreamingContent])

  // Queries
  const { data: conversationsData } = useQuery({
    queryKey: ['trainerConversations'],
    queryFn: conversations.list,
  })

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
  })

  // Load conversation messages
  useEffect(() => {
    if (conversationsData?.length > 0 && !currentConversationId) {
      const latestConversation = conversationsData[0]
      setCurrentConversationId(latestConversation.id)
      loadConversationMessages(latestConversation.id)
    }
  }, [conversationsData, currentConversationId])

  async function loadConversationMessages(conversationId) {
    try {
      const data = await conversations.get(conversationId)
      if (data.messages) {
        const formattedMessages = data.messages.map(msg => ({
          id: msg.id,
          content: msg.content,
          role: msg.role,
          timestamp: msg.createdAt,
          attachmentUrl: msg.attachmentUrl || null,
          attachmentMimeType: msg.attachmentMimeType || null,
        }))
        setMessages(formattedMessages)
        isInitialLoad.current = true
      }
    } catch (error) {
      console.error('Failed to load conversation messages:', error)
    }
  }

  // Scroll handling
  useEffect(() => {
    if (messages.length > 0) {
      const behavior = isInitialLoad.current ? 'instant' : 'smooth'
      messagesEndRef.current?.scrollIntoView({ behavior })
      isInitialLoad.current = false
    }
  }, [messages])

  useEffect(() => {
    if (streamingContent) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [streamingContent])

  // WebSocket connection
  const connectWebSocket = useCallback(async () => {
    try {
      const chatToken = await auth.getChatToken()
      if (!chatToken) {
        console.error('Failed to get chat token')
        return
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/trainer-chat?token=${chatToken}`

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        reconnectAttemptsRef.current = 0
        console.log('WebSocket connected')
        // Heartbeat ping every 30s to keep connection alive on iOS
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'message':
            case 'response':
              setIsTyping(false)
              setStreamingContent('')
              setActiveTools([])
              setMessages(prev => [...prev, {
                id: data.id || Date.now(),
                content: data.content || data.message,
                role: data.role || 'assistant',
                timestamp: data.timestamp || new Date().toISOString(),
                isNew: true,
              }])
              break

            case 'typing':
            case 'status':
              if (data.status === 'typing' || data.type === 'typing') {
                setIsTyping(true)
              }
              break

            case 'connected':
              console.log('WebSocket connection confirmed')
              break

            case 'stream':
              setIsTyping(false)
              streamingContentRef.current += data.content
              scheduleStreamingUpdate()
              break

            case 'stream_end':
              // Cancel any pending batched update
              if (streamRafRef.current) {
                cancelAnimationFrame(streamRafRef.current)
                streamRafRef.current = null
              }
              if (streamingContentRef.current) {
                const finishedTools = streamingToolsRef.current.filter(t => t.status === 'done' || t.status === 'error')
                setMessages(prev => [...prev, {
                  id: data.id || Date.now(),
                  content: streamingContentRef.current + (data.content || ''),
                  role: 'assistant',
                  timestamp: new Date().toISOString(),
                  isNew: true,
                  tools: finishedTools.length > 0 ? finishedTools : undefined,
                }])
                streamingContentRef.current = ''
                streamingToolsRef.current = []
                setStreamingContent('')
              }
              setActiveTools([])
              break

            case 'tool_start':
              setIsTyping(false)
              streamingToolsRef.current = [...streamingToolsRef.current, {
                name: data.tool,
                description: data.description,
                status: 'running'
              }]
              setActiveTools(prev => [...prev, {
                name: data.tool,
                description: data.description,
                status: 'running'
              }])
              break

            case 'tool_end':
              streamingToolsRef.current = streamingToolsRef.current.map(t =>
                t.name === data.tool && t.status === 'running'
                  ? { ...t, status: data.success ? 'done' : 'error', error: data.error || null }
                  : t
              )
              setActiveTools(prev =>
                prev.map(t =>
                  t.name === data.tool
                    ? { ...t, status: data.success ? 'done' : 'error', error: data.error || null }
                    : t
                )
              )
              break

            case 'thinking':
              setIsTyping(true)
              break

            case 'suggestions':
              setSuggestions(data.items || [])
              break

            case 'error':
              console.error('WebSocket error:', data.message)
              setIsTyping(false)
              setActiveTools([])
              // Clear any partial streaming content on error
              if (streamingContentRef.current) {
                streamingContentRef.current = ''
                setStreamingContent('')
              }
              if (streamRafRef.current) {
                cancelAnimationFrame(streamRafRef.current)
                streamRafRef.current = null
              }
              // Show error to user as a system message
              setMessages(prev => [...prev, {
                id: Date.now(),
                content: `⚠️ ${data.message || 'An error occurred. Please try again.'}`,
                role: 'system',
                timestamp: new Date().toISOString(),
                isError: true,
              }])
              break

            case 'conversation_id':
              setCurrentConversationId(data.conversationId)
              queryClient.invalidateQueries({ queryKey: ['trainerConversations'] })
              break

            case 'cancelled':
              setIsTyping(false)
              setActiveTools([])
              if (streamingContentRef.current) {
                streamingContentRef.current = ''
                setStreamingContent('')
              }
              if (streamRafRef.current) {
                cancelAnimationFrame(streamRafRef.current)
                streamRafRef.current = null
              }
              break

            case 'queued':
              // Message queued while AI is still responding - keep typing indicator
              break

            case 'pong':
              break

            default:
              console.log('Unknown message type:', data.type, data)
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = () => {
        setIsConnected(false)
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
        console.log('WebSocket disconnected')
        // Exponential backoff: 3s, 4.5s, 6.75s, ... capped at 30s
        const delay = Math.min(3000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000)
        reconnectAttemptsRef.current++
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket()
        }, delay)
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
    }
  }, [])

  useEffect(() => {
    connectWebSocket()

    // Reconnect when app returns from background (iOS kills WebSocket on suspend)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
          reconnectAttemptsRef.current = 0
          connectWebSocket()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      if (wsRef.current) wsRef.current.close()
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
    }
  }, [connectWebSocket])

  // File handling - supports multiple files
  function handleFileSelect(e) {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Check total file count
    const totalFiles = selectedFiles.length + files.length
    if (totalFiles > MAX_FILES) {
      console.warn(`Maximum ${MAX_FILES} files allowed per message. You have ${selectedFiles.length} selected.`)
      return
    }

    // Validate each file
    const validFiles = []
    const newPreviews = []

    for (const file of files) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        console.warn(`File "${file.name}" type not supported. Allowed types: PDF, images (JPEG, PNG, GIF, WebP), CSV, and text files.`)
        continue
      }

      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File "${file.name}" too large. Maximum size is 20MB.`)
        continue
      }

      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    // Generate previews for images
    validFiles.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          setFilePreviews(prev => {
            const updated = [...prev]
            updated[selectedFiles.length + index] = e.target.result
            return updated
          })
        }
        reader.readAsDataURL(file)
      }
    })

    setSelectedFiles(prev => [...prev, ...validFiles])
    // Reset input so same files can be selected again
    e.target.value = ''
  }

  function clearSelectedFile(index) {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    setFilePreviews(prev => prev.filter((_, i) => i !== index))
  }

  function clearAllFiles() {
    setSelectedFiles([])
    setFilePreviews([])
  }

  // Voice recording
  async function toggleVoiceRecording() {
    if (isRecording) {
      mediaRecorderRef.current?.stop()
      setIsRecording(false)
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const mediaRecorder = new MediaRecorder(stream)
        mediaRecorderRef.current = mediaRecorder

        const chunks = []
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data)
        mediaRecorder.onstop = async () => {
          const blob = new Blob(chunks, { type: 'audio/webm' })
          // TODO: Send to speech-to-text service
          stream.getTracks().forEach(t => t.stop())
        }

        mediaRecorder.start()
        setIsRecording(true)
      } catch (error) {
        console.error('Failed to start recording:', error)
        console.warn('Could not access microphone')
      }
    }
  }

  // Send message - supports multiple files
  async function sendMessage(e) {
    e?.preventDefault()
    if ((!inputValue.trim() && selectedFiles.length === 0) || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    const content = inputValue.trim()
    setInputValue('')
    setSuggestions([])

    // Prepare files data
    let filesData = []
    if (selectedFiles.length > 0) {
      try {
        filesData = await Promise.all(
          selectedFiles.map(async (file, index) => ({
            data: await fileToBase64(file),
            mimeType: file.type,
            fileName: file.name,
          }))
        )
      } catch (error) {
        console.error('Failed to read files:', error)
        console.warn('Failed to read files. Please try again.')
        return
      }
    }

    // Build file attachment info for display
    const fileAttachmentText = selectedFiles.length > 0
      ? ` [Attached: ${selectedFiles.map(f => f.name).join(', ')}]`
      : ''

    // Add user message to UI
    const userMessage = {
      id: Date.now(),
      content: content + fileAttachmentText,
      role: 'user',
      timestamp: new Date().toISOString(),
      files: selectedFiles.length > 0
        ? selectedFiles.map((file, index) => ({
            name: file.name,
            type: file.type,
            preview: filePreviews[index] || null,
          }))
        : null,
    }
    setMessages(prev => [...prev, userMessage])
    clearAllFiles()
    setIsTyping(true)

    // Send to WebSocket - use 'files' array for multiple files
    const messagePayload = {
      type: 'message',
      content,
      conversationId: currentConversationId,
    }
    if (filesData.length > 0) {
      // For backward compatibility, also include single 'file' if only one
      if (filesData.length === 1) {
        messagePayload.file = filesData[0]
      }
      messagePayload.files = filesData
    }
    wsRef.current.send(JSON.stringify(messagePayload))
  }

  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }, [])

  // Handle suggestion click
  function handleSuggestionSelect(prompt) {
    setInputValue(prompt)
    setTimeout(() => {
      sendMessage()
    }, 100)
  }

  // Handle new conversation
  function handleNewConversation() {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'clear' }))
      setMessages([])
      // Use a marker to prevent auto-load effect from picking an old conversation.
      // Server will send back a 'conversation_id' message with the new ID.
      setCurrentConversationId('new')
      setSuggestions([])
      setSidebarOpen(false)
    }
  }

  // Handle conversation select
  function handleConversationSelect(convo) {
    setCurrentConversationId(convo.id)
    loadConversationMessages(convo.id)
    setSidebarOpen(false)
  }

  return (
    <div className="chat-container">
      {/* Conversation Sidebar */}
      <ConversationSidebar
        conversations={conversationsData}
        currentId={currentConversationId}
        onSelect={handleConversationSelect}
        onNew={handleNewConversation}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="chat-header bg-white dark:bg-dial-surface border-b border-gray-200 dark:border-dial-border safe-area-inset-top">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-3">
            {/* Back button - always visible */}
            <button
              onClick={() => navigate('/')}
              className="p-2 text-gray-600 dark:text-indices-medium hover:text-gray-900 dark:hover:text-indices-bright hover:bg-gray-100 dark:hover:bg-dial-elevated rounded-lg transition-colors flex-shrink-0"
              aria-label="Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex-1">
              <h1 className="font-semibold text-gray-900 dark:text-indices-bright text-base leading-tight">AI Assistant</h1>
              <p className="text-xs text-gray-500 dark:text-indices-muted leading-tight">
                {isConnected ? 'Online' : 'Connecting...'}
              </p>
            </div>

            {/* Status indicator */}
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isConnected ? 'bg-green-500 dark:bg-lume-teal' : 'bg-gray-400 dark:bg-indices-muted'}`} />

            {/* AI Tests button */}
            <button
              onClick={() => navigate('/ai-settings')}
              className="p-2 text-gray-600 dark:text-indices-medium hover:text-gray-900 dark:hover:text-indices-bright hover:bg-gray-100 dark:hover:bg-dial-elevated rounded-lg transition-colors flex-shrink-0"
              title="AI Tests & Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Conversations button - right side */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 dark:text-indices-medium hover:text-gray-900 dark:hover:text-indices-bright hover:bg-gray-100 dark:hover:bg-dial-elevated rounded-lg transition-colors flex items-center gap-1 flex-shrink-0"
              title="Conversations"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span className="hidden sm:inline text-sm">Chats</span>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages px-3 sm:px-4 py-2 sm:py-4">
          {messages.length === 0 && !isTyping && activeTools.length === 0 && (
            <div className="text-center text-gray-500 dark:text-indices-muted mt-8">
              <div className="w-16 h-16 bg-primary-100 dark:bg-lume-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600 dark:text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="font-medium text-gray-900 dark:text-indices-bright">AI Training Assistant</p>
              <p className="text-sm mt-1">Ask me about training programs, client management, or any fitness-related questions.</p>

              {/* Quick Start Suggestions */}
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {[
                  { label: 'View my clients', prompt: 'Show me all my clients' },
                  { label: 'Create a workout', prompt: 'Help me create a new workout template' },
                  { label: 'Knowledge base', prompt: 'Show my knowledge base articles' },
                ].map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionSelect(item.prompt)}
                    className="px-4 py-2 bg-white dark:bg-dial-elevated border border-gray-200 dark:border-dial-border rounded-full text-sm text-gray-700 dark:text-indices-medium hover:bg-gray-50 dark:hover:bg-dial-surface hover:border-primary-300 dark:hover:border-lume-teal/50 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message, index) => {
            const prevMessage = messages[index - 1]
            const showDate = !prevMessage || formatDate(message.timestamp) !== formatDate(prevMessage.timestamp)

            return (
              <div key={message.id}>
                {showDate && (
                  <div className="text-center text-xs text-gray-500 dark:text-indices-muted my-4">
                    {formatDate(message.timestamp)}
                  </div>
                )}
                <Message
                  message={message}
                  isUser={message.role === 'user'}
                  isNew={message.isNew}
                />
              </div>
            )
          })}

          {/* Tool Indicator */}
          {activeTools.length > 0 && <ToolIndicator tools={activeTools} />}

          {/* Streaming message */}
          {streamingContent && (
            <Message
              message={{ content: streamingContent, timestamp: new Date().toISOString() }}
              isUser={false}
              isStreaming={true}
            />
          )}

          {isTyping && activeTools.length === 0 && <TypingIndicator />}

          {/* Suggested Actions */}
          <SuggestedActions
            suggestions={suggestions}
            onSelect={handleSuggestionSelect}
          />

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <SmartInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={sendMessage}
          onFileSelect={handleFileSelect}
          selectedFiles={selectedFiles}
          onClearFile={clearSelectedFile}
          onClearAllFiles={clearAllFiles}
          filePreviews={filePreviews}
          disabled={!isConnected}
          isRecording={isRecording}
          onVoiceToggle={toggleVoiceRecording}
          clients={clientsData}
        />
      </div>
    </div>
  )
}
